/**
 * RecentDecisions — chronological list of founder/admin approvals.
 *
 * Click an entry → onOpenCard(card_id). Monochrome chrome; actor
 * action survives as a small uppercase tag, no status colour.
 */
import { cn } from '../../lib/utils';
import type { RecentDecisionsResponse } from './dashboard-types';

export interface RecentDecisionsProps {
  data: RecentDecisionsResponse | undefined;
  isLoading?: boolean | undefined;
  onOpenCard?: ((cardId: string, boardId?: string) => void) | undefined;
  className?: string | undefined;
}

const ACTION_TAG: Record<string, string> = {
  approve: 'Approved',
  reject: 'Rejected',
  reroute: 'Re-routed',
  skip: 'Skipped',
  delegate: 'Delegated',
};

export function RecentDecisions({ data, isLoading, onOpenCard, className }: RecentDecisionsProps) {
  const decisions = data?.decisions ?? [];
  return (
    <section
      aria-labelledby="decisions-heading"
      className={cn('rounded-md border border-border bg-background p-4', className)}
    >
      <header className="mb-3 flex items-baseline justify-between">
        <h2 id="decisions-heading" className="text-sm font-semibold text-foreground">
          Recent decisions
        </h2>
        <span className="text-2xs text-muted-foreground">{decisions.length} total</span>
      </header>
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : decisions.length === 0 ? (
        <p className="rounded border border-dashed border-border bg-foreground/[0.02] p-4 text-center text-xs text-muted-foreground">
          No recent decisions.
        </p>
      ) : (
        <ul className="divide-y divide-border text-xs">
          {decisions.map((d) => (
            <li key={d.id} className="flex items-center gap-2 py-2">
              <span className="rounded border border-border bg-foreground/[0.04] px-1.5 py-0.5 text-2xs uppercase tracking-wide text-muted-foreground">
                {ACTION_TAG[d.action] ?? d.action}
              </span>
              <span className="truncate text-2xs text-muted-foreground">{d.actor_name}</span>
              <button
                type="button"
                onClick={() => d.card_id && onOpenCard?.(d.card_id)}
                disabled={!d.card_id}
                className="flex-1 truncate text-left text-xs text-foreground transition-colors hover:underline disabled:text-muted-foreground"
                title={d.card_title ?? d.notes ?? ''}
              >
                {d.card_title ?? '(no card)'}
              </button>
              <span className="shrink-0 text-2xs text-muted-foreground">
                {formatRelative(d.decided_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}
